import React, { useEffect, useContext } from "react";
import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./utils/i18n"; // i18n yapılandırmasını yükle

// Tema
import theme from "./styles/theme";

// Sayfa geçiş sistemi için TransitionProvider'ı içe aktar
import { TransitionContext, TransitionProvider } from "./context/TransitionContext";

// Layout bileşenleri
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";

// Sayfalar
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import QuotePage from "./pages/QuotePage";

// Ürün Detay Sayfaları
import WheatPage from "./pages/ProductDetail/WheatPage";
import BarleyPage from "./pages/ProductDetail/BarleyPage";
import CornPage from "./pages/ProductDetail/CornPage";
import CornOilPage from "./pages/ProductDetail/CornOilPage";
import SunflowerOilPage from "./pages/ProductDetail/SunflowerOilPage";
import FertilizerPage from "./pages/ProductDetail/FertilizerPage";
import ChickenPage from "./pages/ProductDetail/ChickenPage";

// ScrollToTop bileşeni - Sayfa geçişleriyle koordine çalışacak şekilde güncellendi
function ScrollToTop() {
  const { pathname } = useLocation();
  const { isTransitioning } = useContext(TransitionContext);

  useEffect(() => {
    // Sadece geçiş tamamlandığında sayfayı en üste kaydır
    if (!isTransitioning) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [pathname, isTransitioning]);

  return null;
}

// Ana uygulama yapılandırması
function AppContent() {
  const { i18n } = useTranslation();

  // Sayfa yüklendiğinde sayfa başlığını ayarla
  useEffect(() => {
    document.title =
      "Safe Global Trade | " +
      (i18n.language === "tr"
        ? "Uluslararası Ticarette Güvenilir Ortağınız"
        : "Your Reliable Partner in Global Trade");

    // HTML etiketinin dilini ayarla
    document.documentElement.lang = i18n.language || "tr";

    // Google Chrome çeviri önerisini engellemek için meta etiket
    let metaLang = document.querySelector(
      'meta[http-equiv="Content-Language"]'
    );
    if (!metaLang) {
      metaLang = document.createElement("meta");
      metaLang.httpEquiv = "Content-Language";
      document.head.appendChild(metaLang);
    }
    metaLang.content = i18n.language || "tr";
  }, [i18n.language]);

  return (
    <>
      <ScrollToTop />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          overflowY: "visible",
          position: "relative",
        }}
      >
        <Header />
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1,
            width: "100%",
            maxWidth: "100vw",
            pt: { xs: 7, md: 8 },
            overflowX: "hidden",
            overflowY: "visible",
            position: "relative",
            // Sayfa geçişlerinde içeriğin animasyonu için stil eklendi
            transition: "opacity 0.4s ease, transform 0.3s ease"
          }}
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/quote" element={<QuotePage />} />

            {/* Ürün Detay Sayfaları */}
            <Route path="/products/wheat" element={<WheatPage />} />
            <Route path="/products/barley" element={<BarleyPage />} />
            <Route path="/products/corn" element={<CornPage />} />
            <Route path="/products/corn-oil" element={<CornOilPage />} />
            <Route
              path="/products/sunflower-oil"
              element={<SunflowerOilPage />}
            />
            <Route path="/products/fertilizer" element={<FertilizerPage />} />
            <Route path="/products/chicken" element={<ChickenPage />} />

            {/* Bilinmeyen URL'ler için ana sayfaya yönlendirme */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </>
  );
}

function App() {
  const { i18n } = useTranslation();

  // Dil ayarı için
  useEffect(() => {
    // Uygulamanın başlangıcında dili Türkçe olarak ayarla
    i18n.changeLanguage("tr");
    localStorage.setItem("i18nextLng", "tr");

    // HTML etiketlerini güncelle
    document.documentElement.lang = "tr";

    // Google Chrome çeviri önerisini engellemek için
    const metaLang = document.createElement("meta");
    metaLang.httpEquiv = "Content-Language";
    metaLang.content = "tr";
    document.head.appendChild(metaLang);
    
    // Mobil scroll ve sayfa geçiş animasyonları için stil eklentisi
    const style = document.createElement('style');
    style.textContent = `
      /* Sayfa geçiş animasyonları için stiller */
      .page-transition-out {
        opacity: 0;
        transform: translateY(20px);
        transition: opacity 300ms ease-out, transform 300ms ease-out;
      }
      
      .page-transition-in {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 500ms ease-in, transform 500ms ease-in;
      }
      
      /* Mobil scroll düzeltmeleri */
      html, body {
        overflow-x: hidden !important;
        overflow-y: auto !important;
        height: auto !important;
        width: 100%;
        max-width: 100vw;
        position: relative;
      }
      
      #root {
        overflow-x: hidden !important;
        overflow-y: visible !important;
        min-height: 100vh;
        height: auto !important;
        width: 100%;
        max-width: 100vw;
      }
      
      /* Tüm container'lar için */
      * {
        max-width: 100vw;
      }
      
      /* iOS momentum scrolling */
      body {
        -webkit-overflow-scrolling: touch;
      }
    `;
    document.head.appendChild(style);
    
  }, [i18n]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* TransitionProvider ile HashRouter'ı sarmalayın */}
      <TransitionProvider>
        <HashRouter>
          <AppContent />
        </HashRouter>
      </TransitionProvider>
    </ThemeProvider>
  );
}

export default App;